import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const navItems = [
  { label: "首页", href: "/" },
  {
    label: "服务方案",
    children: [
      { label: "家庭安全", href: "/category/family" },
      { label: "企业防护", href: "/category/business" },
      { label: "个人保障", href: "/category/personal" },
    ],
  },
  { label: "安全百科", href: "/category/family" },
  { label: "关于我们", href: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-primary-500 rounded-lg flex items-center justify-center">
              <i className="ri-shield-check-line text-xl text-background-50"></i>
            </div>
            <span className="text-xl font-bold text-foreground-900 font-heading whitespace-nowrap">
              安大师
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) =>
              item.href ? (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                    location.pathname === item.href
                      ? "text-primary-600 bg-primary-50"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                  }`}
                >
                  {item.label}
                </Link>
              ) : (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setDropdownOpen(true)}
                  onMouseLeave={() => setDropdownOpen(false)}
                >
                  <button
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap flex items-center gap-1 ${
                      dropdownOpen
                        ? "text-primary-600 bg-primary-50"
                        : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                    }`}
                  >
                    {item.label}
                    <i className="ri-arrow-down-s-line text-xs"></i>
                  </button>
                  {dropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 bg-background-50 rounded-lg border border-background-200/70 py-2 w-40 animate-fade-in">
                      {item.children?.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="block px-4 py-2 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )
            )}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:400-888-9999"
              className="flex items-center gap-1.5 text-sm text-foreground-700 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              <span>400-888-9999</span>
            </a>
            <Link
              to="/about"
              className="bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap"
            >
              免费咨询
            </Link>
          </div>

          <button
            className="md:hidden w-9 h-9 flex items-center justify-center text-foreground-800"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="菜单"
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-fade-in-down">
          <div className="px-4 py-4 flex flex-col gap-1">
            {navItems.map((item) =>
              item.href ? (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`px-4 py-3 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                    location.pathname === item.href
                      ? "text-primary-600 bg-primary-50"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                  }`}
                >
                  {item.label}
                </Link>
              ) : (
                <div key={item.label}>
                  <div className="px-4 py-2 text-xs font-medium text-foreground-500 uppercase tracking-wide">
                    {item.label}
                  </div>
                  {item.children?.map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      className="block pl-8 pr-4 py-2.5 rounded-md text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              )
            )}
            <div className="mt-3 pt-3 border-t border-background-200/70">
              <a
                href="tel:400-888-9999"
                className="flex items-center gap-2 px-4 py-3 text-sm text-foreground-700 whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                <span>400-888-9999</span>
              </a>
              <Link
                to="/about"
                className="block mx-4 mt-2 text-center bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-3 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap"
              >
                免费咨询
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}